import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  Skeleton,
  Alert,
  AlertIcon,
  Code,
  SimpleGrid,
  Tooltip,
  IconButton,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import { useTranslation } from 'react-i18next'
import { useRefundOperation } from '@/hooks/useAdminData'
import { RefundOperation } from '@/types'
import { StatusBadge } from '@/components/common/StatusBadge'
import { formatChainName, getChainPrefix } from '@/constants/chains'
import { formatRefundAmount, formatDateTime, getExplorerTxUrl } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface RefundDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  refundId: string
}

// Separate component for refund meta items to avoid hooks in map
const RefundMetaItem = ({
  meta,
  index,
  chainId,
  tokenAddress,
  tokenSymbol,
  tokenDecimals,
  t,
}: {
  meta: any
  index: number
  chainId: string | undefined | null
  tokenAddress?: string
  tokenSymbol?: string
  tokenDecimals?: number
  t: any
}) => {
  const metaBgColor = useColorModeValue('gray.50', 'gray.700')
  const metaBorderColor = useColorModeValue('gray.200', 'gray.600')

  return (
    <Box
      p={1.5}
      bg={metaBgColor}
      borderRadius="sm"
      border="1px"
      borderColor={metaBorderColor}
    >
      <Text fontSize="xs" fontWeight="medium" mb={0.5}>
        {t('refunds.details.refundMeta')} {index + 1}
      </Text>
      <VStack spacing={0} align="stretch">
        <DetailRow
          label={t('refunds.details.amount')}
          value={formatRefundAmount(
            meta.amount || '0',
            chainId || '0',
            tokenAddress,
            tokenSymbol,
            tokenDecimals
          )}
        />
        <DetailRow
          label={t('refunds.details.recipient')}
          value={meta.recipient || meta.to_address}
          isCode
          copyable
        />
      </VStack>
    </Box>
  )
}

const DetailRow = ({
  label,
  value,
  isCode = false,
  copyable = false,
  explorerUrl,
}: {
  label: string
  value: string | number | null | undefined
  isCode?: boolean
  copyable?: boolean
  explorerUrl?: string
}) => {
  const toast = useToast()
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const codeColor = useColorModeValue('gray.800', 'gray.100')
  const codeBgColor = useColorModeValue('gray.100', 'gray.700')

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Copy failed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const displayValue = value ?? 'N/A'

  return (
    <HStack spacing={2} py={0.5} align="start" width="100%">
      <Text fontWeight="medium" color={textColor} fontSize="xs" minW="100px" flexShrink={0}>
        {label}
      </Text>
      <Box flex="1" minW="0" overflow="hidden">
        <HStack spacing={1} flexWrap="wrap">
          {isCode ? (
            <Code
              fontSize="xs"
              px={1}
              py={0}
              color={codeColor}
              bg={codeBgColor}
              wordBreak="break-all"
              display="inline-block"
              maxW="100%"
            >
              {displayValue}
            </Code>
          ) : (
            <Text
              fontSize="xs"
              fontWeight={value ? 'medium' : 'normal'}
              color={value ? 'inherit' : 'gray.500'}
              wordBreak="break-word"
            >
              {displayValue}
            </Text>
          )}

          {copyable && value && (
            <Tooltip label="Copy to clipboard">
              <IconButton
                aria-label="Copy"
                icon={<CopyIcon />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(String(value))}
                flexShrink={0}
              />
            </Tooltip>
          )}

          {explorerUrl && (
            <Tooltip label="View on explorer">
              <IconButton
                aria-label="View on explorer"
                icon={<ExternalLinkIcon />}
                size="xs"
                variant="ghost"
                onClick={() => window.open(explorerUrl, '_blank')}
                flexShrink={0}
              />
            </Tooltip>
          )}
        </HStack>
      </Box>
    </HStack>
  )
}

export function RefundDetailsModal({ isOpen, onClose, refundId }: RefundDetailsModalProps) {
  const { t } = useTranslation()
  const { data: refund, isLoading, isFetching, error, refetch } = useRefundOperation(refundId)

  // All color mode values must be at the top level
  const grayTextColor = useColorModeValue('gray.600', 'gray.300')
  const grayBg50 = useColorModeValue('gray.50', 'gray.700')
  const blueBg50 = useColorModeValue('blue.50', 'blue.900')

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'ros_pending': t('refunds.status.pending'),
      'ros_refunded': t('refunds.status.refunded'),
      'ros_failed': t('refunds.status.failed'),
    }
    return statusMap[status] || status
  }

  const resolveChainLabel = (refund?: RefundOperation | null) => {
    if (!refund) {
      return t('common.unknown')
    }

    const chainIdCandidate =
      refund.chain_numeric_id ??
      (refund as any).chainNumericId ??
      refund.chain_id ??
      (refund as any).chainId ??
      undefined

    const chainNameCandidate =
      refund.chain_name ??
      (refund as any).chainName ??
      refund.chain_code ??
      (refund as any).chainCode ??
      undefined

    if (chainIdCandidate) {
      try {
        const formatted = formatChainName(chainIdCandidate, 'full')
        if (formatted && formatted.toLowerCase() !== 'unknown') {
          return formatted
        }
      } catch (error) {
        console.warn(
          'Error formatting chain name:',
          error,
          'chainId:',
          chainIdCandidate
        )
      }
    }

    if (chainNameCandidate) {
      try {
        const formatted = formatChainName(chainNameCandidate, 'full')
        if (formatted && formatted.toLowerCase() !== 'unknown') {
          return formatted
        }
      } catch (error) {
        console.warn(
          'Error formatting chain name:',
          error,
          'chainName:',
          chainNameCandidate
        )
      }
      return chainNameCandidate
    }

    if (chainIdCandidate) {
      return `Chain ${chainIdCandidate}`
    }

    return t('common.unknown')
  }

  const formatAmount = (refund: RefundOperation) => {
    const amountInSmallestUnit = refund.total_refund_amount || refund.refund_amount || '0'
    return formatRefundAmount(
      amountInSmallestUnit,
      refund.chain_id,
      refund.token_address,
      refund.token_symbol,
      refund.tokenDecimals ?? refund.token_decimals
    )
  }

  const isSolanaRefund = (refund?: RefundOperation | null) => {
    if (!refund) {
      return false
    }
    return getChainPrefix(
      refund.chain_numeric_id ??
      (refund as any).chainNumericId ??
      refund.chain_id ??
      (refund as any).chainId ??
      refund.chain_code ??
      (refund as any).chainCode
    ) === 'solana'
  }

  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={12}>
            <HStack justify="space-between" spacing={3}>
              <Text>{t('refunds.details.title')}</Text>
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void refetch()}
                isLoading={isFetching}
                isDisabled={isFetching}
                flexShrink={0}
              />
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error">
              <AlertIcon />
              {t('refunds.details.loadError')}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>
              {t('common.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW={{ base: 'calc(100vw - 24px)', xl: '1120px' }}>
        <ModalHeader pr={12}>
          <HStack justify="space-between" spacing={3}>
            <HStack>
              <Text>{t('refunds.details.title')}</Text>
              {refund && (
                <StatusBadge
                  statusType="refund"
                  status={refund.status}
                  label={getStatusText(refund.status)}
                />
              )}
            </HStack>
            <RefreshIconButton
              label={t('common.refresh')}
              tooltipPlacement="left"
              onClick={() => void refetch()}
              isLoading={isFetching}
              isDisabled={isFetching}
              flexShrink={0}
            />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={2} maxH="70vh" overflowY="auto" overflowX="hidden">
          <VStack spacing={2} align="stretch">
            {isLoading ? (
              <VStack spacing={4}>
                <Skeleton height="20px" />
                <Skeleton height="20px" />
                <Skeleton height="20px" />
                <Skeleton height="20px" />
              </VStack>
            ) : refund ? (
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} alignItems="start">
                <VStack spacing={3} align="stretch">
                  {/* Basic Information */}
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                      {t('refunds.details.basicInfo')}
                    </Text>
                    <VStack spacing={0} align="stretch">
                      <DetailRow
                        label={t('refunds.details.orderId')}
                        value={refund.orderId || refund.order_id}
                        isCode
                        copyable
                      />
                      {refund.merchant_name && (
                        <DetailRow
                          label={t('refunds.details.merchantName')}
                          value={refund.merchant_name}
                        />
                      )}
                      <DetailRow
                        label={t('refunds.details.chain')}
                        value={resolveChainLabel(refund)}
                      />
                      <DetailRow
                        label={t('refunds.details.refundAmount')}
                        value={formatAmount(refund)}
                      />
                      {refund.refund_meta_count && refund.refund_meta_count > 1 && (
                        <DetailRow
                          label={t('refunds.table.multipleTransactions')}
                          value={refund.refund_meta_count}
                        />
                      )}
                    </VStack>
                  </Box>

                  {/* Refund Breakdown (for multiple refunds) */}
                  {refund.refund_meta && refund.refund_meta.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontSize="xs" fontWeight="bold" mb={1}>
                          {t('refunds.details.refundBreakdown')}
                        </Text>
                        <VStack spacing={1} align="stretch">
                          {refund.refund_meta.map((meta, index) => (
                            <RefundMetaItem
                              key={index}
                              meta={meta}
                              index={index}
                              chainId={refund.chain_id}
                              tokenAddress={refund.token_address}
                              tokenSymbol={refund.token_symbol}
                              tokenDecimals={refund.tokenDecimals ?? refund.token_decimals}
                              t={t}
                            />
                          ))}
                        </VStack>
                      </Box>
                    </>
                  )}
                </VStack>

                <VStack spacing={3} align="stretch">
                  {/* Transaction Information */}
                  {refund.refund_tx_hash && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb={1}>
                        {t('refunds.details.transactionInfo')}
                      </Text>
                      <VStack spacing={0} align="stretch">
                        <DetailRow
                          label={t('refunds.details.txHash')}
                          value={refund.refund_tx_hash}
                          isCode
                          copyable
                          explorerUrl={getExplorerTxUrl(refund.chain_id, refund.refund_tx_hash)}
                        />
                        {refund.refund_block_number && (
                          <DetailRow
                            label={t('common.blockOrSlot', { defaultValue: 'Block / Slot' })}
                            value={refund.refund_block_number}
                          />
                        )}
                        {isSolanaRefund(refund) && (refund as any).refund_slot && (
                          <DetailRow
                            label={t('common.blockOrSlot', { defaultValue: 'Block / Slot' })}
                            value={(refund as any).refund_slot}
                          />
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* Verification Information */}
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                      {t('refunds.details.verificationInfo')}
                    </Text>
                    <VStack spacing={0} align="stretch">
                      <HStack spacing={2} py={0.5} align="start">
                        <Text fontWeight="medium" fontSize="xs" color={grayTextColor} minW="100px">
                          {t('refunds.details.verified')}
                        </Text>
                        <Badge colorScheme={refund.is_verified ? 'green' : 'gray'} size="sm">
                          {refund.is_verified ? t('common.yes') : t('common.no')}
                        </Badge>
                      </HStack>
                      {refund.verified_at && (
                        <DetailRow
                          label={t('refunds.details.verifiedAt')}
                          value={formatDateTime(refund.verified_at)}
                        />
                      )}
                      {refund.verification_soft_retry_count && refund.verification_soft_retry_count > 0 && (
                        <DetailRow
                          label={t('refunds.details.retryCount')}
                          value={refund.verification_soft_retry_count}
                        />
                      )}
                      {refund.last_verification_check_at && (
                        <DetailRow
                          label={t('refunds.details.lastCheckAt')}
                          value={formatDateTime(refund.last_verification_check_at)}
                        />
                      )}
                    </VStack>
                  </Box>

                  {/* Timestamps and Notes */}
                  {(refund.created_at || refund.refund_notes || refund.verification_notes || refund.error_message) && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" mb={1}>
                        {t('refunds.details.additionalInfo')}
                      </Text>
                      <VStack spacing={0} align="stretch">
                        <DetailRow
                          label={t('refunds.details.createdAt')}
                          value={formatDateTime(refund.created_at)}
                        />
                        {refund.refunded_by && (
                          <DetailRow
                            label={t('refunds.details.refundedBy')}
                            value={refund.refunded_by}
                          />
                        )}
                        {refund.refund_notes && (
                          <HStack spacing={2} align="start" py={0.5}>
                            <Text fontWeight="medium" fontSize="xs" color={grayTextColor} minW="100px">
                              {t('refunds.details.refundNotes')}
                            </Text>
                            <Box flex="1" minW="0" overflow="hidden">
                              <Box p={1} bg={grayBg50} borderRadius="sm" overflow="hidden">
                                <Text fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                  {refund.refund_notes}
                                </Text>
                              </Box>
                            </Box>
                          </HStack>
                        )}
                        {refund.verification_notes && (
                          <HStack spacing={2} align="start" py={0.5}>
                            <Text fontWeight="medium" fontSize="xs" color={grayTextColor} minW="100px">
                              {t('refunds.details.verificationNotes')}
                            </Text>
                            <Box flex="1" minW="0" overflow="hidden">
                              {Array.isArray(refund.verification_notes) ? (
                                <VStack spacing={1} align="stretch">
                                  {refund.verification_notes.map((note: any, idx: number) => (
                                    <Box key={idx} p={1} bg={blueBg50} borderRadius="sm" overflow="hidden">
                                      <HStack spacing={2} flexWrap="wrap">
                                        <Text fontSize="xs" fontWeight="bold">
                                          {formatDateTime(note.timestamp)}
                                        </Text>
                                        <Badge size="sm" colorScheme={note.type === 'error' ? 'red' : 'blue'}>
                                          {note.type}
                                        </Badge>
                                      </HStack>
                                      <Text fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                        {note.note}
                                      </Text>
                                    </Box>
                                  ))}
                                </VStack>
                              ) : (
                                <Box p={1} bg={blueBg50} borderRadius="sm" overflow="hidden">
                                  <Text fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word">
                                    {refund.verification_notes}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          </HStack>
                        )}
                        {refund.error_message && (
                          <HStack spacing={2} align="start" py={0.5}>
                            <Text fontWeight="medium" fontSize="xs" color={grayTextColor} minW="100px">
                              {t('refunds.details.errorMessage')}
                            </Text>
                            <Box flex="1" minW="0" overflow="hidden">
                              <Alert status="error" size="sm" py={0.5}>
                                <AlertIcon boxSize={3} />
                                <Text fontSize="xs" wordBreak="break-word">
                                  {refund.error_message}
                                </Text>
                              </Alert>
                            </Box>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </SimpleGrid>
            ) : (
              <Alert status="warning">
                <AlertIcon />
                {t('refunds.details.notFound')}
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter py={3}>
          <Button onClick={onClose} size="sm">
            {t('common.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
